import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { SUBJECT_META } from "@/lib/scale";
import { toast } from "sonner";
import DocHeader from "@/components/DocHeader";
import { ArrowLeft, Printer, Loader2, Sprout, Sparkles, Save, Plus } from "lucide-react";

// Colours taken directly from the uploaded templates
const T = {
  green: "#B3E5A1",      // profile section header fill (accent6 tint)
  greenDeep: "#7FB069",
  slate: "#44546A",      // theme dark text/accent
  grey: "#F2F2F2",       // targets template light row
  greyMid: "#E7E6E6",    // targets template header
};

const STAGES = ["Classroom Support", "School Support", "School Support Plus"];

// NEPS-style document heading accents: Support Plan = red, Support Review = yellow
let BAND_ACCENT = { band: T.green, bandText: T.slate };
const OUTCOMES = ["Achieved", "Partially achieved", "Not yet achieved"];
const OUTCOME_COLOR = {
  "Achieved": "#7FB069",
  "Partially achieved": "#E6B800",
  "Not yet achieved": "#D98A8A",
};

const EMPTY_PLAN = { stage: STAGES[0], strengths: "", concerns: "", targets: [], personnel: "", notes: "", sign_date: "", review_date: "" };
const EMPTY_REVIEW = { review_summary: "", target_reviews: [], what_worked: "", what_to_change: "", next_steps: "", sign_date: "", review_date: "" };

const uid = () => Math.random().toString(36).slice(2, 10);
const withKeys = (arr) => (arr || []).map((t) => (t._k ? t : { ...t, _k: uid() }));
const normalize = (c) => ({ ...c, targets: withKeys(c.targets), target_reviews: withKeys(c.target_reviews) });

function outcomeFor(t) {
  if (t.status === "achieved" || t.progress === "achieved") return "Achieved";
  if (t.status === "in_progress" || t.progress === "acquiring" || t.progress === "becoming_fluent") return "Partially achieved";
  return "Not yet achieved";
}

export default function SupportDoc({ docType }) {
  const isPlan = docType === "plan";
  const accent = isPlan
    ? { band: "#F4B6AE", bandText: "#7A2417", title: "#B23A2E", border: "#E0897B" }
    : { band: "#F6E08A", bandText: "#6E5410", title: "#A9791A", border: "#E6CB52" };
  BAND_ACCENT = { band: accent.band, bandText: accent.bandText };
  const { studentId } = useParams();
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [student, setStudent] = useState(null);
  const [content, setContent] = useState(isPlan ? EMPTY_PLAN : EMPTY_REVIEW);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/students/${studentId}`).then((r) => setStudent(r.data)).catch(() => navigate("/"));
    api.get(`/support-docs/${studentId}/${docType}`).then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      if (r.data && r.data.content) {
        setContent(normalize({ ...r.data.content, sign_date: r.data.content.sign_date || today }));
      } else {
        setContent((c) => ({ ...c, sign_date: today }));
      }
    }).catch(() => {});
  }, [studentId, docType, navigate]);
  useEffect(() => { load(); }, [load]);

  const set = (field, val) => setContent((c) => ({ ...c, [field]: val }));

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/suggest/support-${docType}/${studentId}`);
      setContent((c) => normalize({ ...data.content, sign_date: c.sign_date, review_date: c.review_date }));
      toast.success("AI draft ready — review and edit as needed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/support-docs/${studentId}/${docType}`, { content });
      toast.success("Saved");
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!student)
    return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const title = isPlan ? "Student Support Plan" : "Support Review Record";
  const studentTargets = (student.targets || []).filter((t) => t.priority);

  const resolve = (t) => ({
    subject: SUBJECT_META[t.subject]?.name || t.subject,
    area: curriculum?.subjects?.[t.subject]?.domains.find((d) => d.key === t.domain)?.name || t.domain,
  });

  const addPlanTarget = (t) => {
    if ((content.targets || []).some((x) => x.source_target_id === t.id)) return;
    const r = resolve(t);
    set("targets", [...(content.targets || []), {
      _k: uid(), subject: r.subject, area: r.area, target: t.text,
      strategies: t.strategies || "", resources: t.resources || "", source_target_id: t.id,
    }]);
  };

  const addReviewTarget = (t) => {
    if ((content.target_reviews || []).some((x) => x.source_target_id === t.id)) return;
    const r = resolve(t);
    const outcome = outcomeFor(t);
    set("target_reviews", [...(content.target_reviews || []), {
      _k: uid(), subject: r.subject, area: r.area, target: t.text, outcome, evidence: "", source_target_id: t.id,
    }]);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      {/* Toolbar */}
      <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <button data-testid="support-back-btn" onClick={() => navigate(`/student/${studentId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition">
          <ArrowLeft size={16} /> Back to profile
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="support-generate-btn" onClick={generate} disabled={generating}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
            style={{ background: `linear-gradient(135deg,${T.greenDeep},#5d8a5a)` }}>
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? "Generating…" : "Generate with AI"}
          </button>
          <button data-testid="support-save-btn" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#7FB069] transition disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
          </button>
          <button data-testid="support-print-btn" onClick={() => window.print()}
            className="text-white rounded-full px-5 py-2 font-semibold font-['Outfit'] transition flex items-center gap-2"
            style={{ backgroundColor: T.slate }}>
            <Printer size={17} /> Print
          </button>
        </div>
      </div>

      {/* Sheet */}
      <div className="print-area max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] overflow-hidden">
        <div className="px-8 sm:px-10 pt-8"><DocHeader printOnly subtitle="Student Support Plan" /></div>
        <div className="px-8 sm:px-10 pt-8 pb-5 flex items-center justify-between" style={{ borderBottom: `3px solid ${accent.border}` }}>
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: T.greenDeep }}><Sprout size={18} /></span>
            <span className="font-['Outfit'] font-bold text-lg" style={{ color: T.slate }}>BloomTrack</span>
          </div>
          <p className="text-sm text-slate-400">Continuum of Support</p>
        </div>

        <div className="px-8 sm:px-10 py-7">
          <h1 className="font-['Outfit'] text-3xl font-bold" style={{ color: accent.title }}>{title}</h1>

          {/* Student details */}
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-5 rounded-xl p-4" style={{ backgroundColor: T.grey }}>
            <Detail label="Name" value={student.name} />
            <Detail label="Class / Level" value={`${student.class_level || ""}${student.class_name ? " · " + student.class_name : ""}`} />
            <DateField label="Date" testId="support-date-input" value={content.sign_date || ""} onChange={(v) => set("sign_date", v)} />
            <DateField label={isPlan ? "Review Date" : "Date of Review"} testId="support-review-date-input" value={content.review_date || ""} onChange={(v) => set("review_date", v)} />
          </div>

          {isPlan
            ? <PlanBody content={content} set={set} studentTargets={studentTargets} addPlanTarget={addPlanTarget} />
            : <ReviewBody content={content} set={set} studentTargets={studentTargets} addReviewTarget={addReviewTarget} resolve={resolve} />}

          {/* Sign-off */}
          <div className="mt-9 pt-6 grid sm:grid-cols-2 gap-x-8 gap-y-8" style={{ borderTop: `2px solid ${T.greyMid}` }}>
            <SignLine label="Signature of Parent(s)/Guardian(s)" />
            <SignLine label="Signature of Class Teacher" />
            <SignLine label="Signature of Support Teacher (SET)" />
            <SignLine label="Agreed Next Review Date" />
          </div>

          <p className="text-xs text-slate-400 mt-9 pt-5 text-center" style={{ borderTop: `1px solid ${T.grey}` }}>
            BloomTrack · {title} · Aligned to the Irish primary curriculum &amp; NEPS Continuum of Support
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Plan body ---------------- */
function PlanBody({ content, set, studentTargets, addPlanTarget }) {
  const addTarget = () => set("targets", [...(content.targets || []), { _k: uid(), subject: "", area: "", target: "", strategies: "", resources: "" }]);
  const updTarget = (i, f, v) => set("targets", content.targets.map((t, idx) => (idx === i ? { ...t, [f]: v } : t)));
  const delTarget = (i) => set("targets", content.targets.filter((_, idx) => idx !== i));
  const chosenIds = new Set((content.targets || []).map((t) => t.source_target_id).filter(Boolean));

  return (
    <>
      <Band>Stage of Support</Band>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="support-stage">
        {STAGES.map((s) => {
          const active = content.stage === s;
          return (
            <button key={s} data-testid={`stage-${s.replace(/\s+/g, "-")}`} onClick={() => set("stage", s)}
              className="text-sm rounded-lg border px-3.5 py-2 font-medium transition"
              style={active ? { backgroundColor: T.greenDeep, borderColor: T.greenDeep, color: "#fff" }
                            : { borderColor: "#cbd5e1", color: "#475569" }}>
              {s}
            </button>
          );
        })}
      </div>

      <Band>Strengths &amp; Interests</Band>
      <Area testId="plan-strengths" value={content.strengths} onChange={(v) => set("strengths", v)} rows={3} />

      <Band>Priority Needs / Areas of Concern</Band>
      <Area testId="plan-concerns" value={content.concerns} onChange={(v) => set("concerns", v)} rows={3} />

      <Band>Targets, Strategies &amp; Resources</Band>
      <TargetPicker studentTargets={studentTargets} chosenIds={chosenIds} onAdd={addPlanTarget} testPrefix="plan" />
      <div className="space-y-3 mt-3">
        {(content.targets || []).map((t, i) => (
          <div key={t._k || i} data-testid={`plan-target-${i}`} className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold font-['Outfit']" style={{ color: T.slate }}>{targetLabel(t, i)}</span>
              <button onClick={() => delTarget(i)} className="text-xs text-red-400 hover:text-red-600 no-print">Remove</button>
            </div>
            <MiniField label="Subject" value={t.subject || ""} onChange={(v) => updTarget(i, "subject", v)} />
            <MiniField label="Skill area" value={t.area} onChange={(v) => updTarget(i, "area", v)} />
            <MiniArea label="Target" value={t.target} onChange={(v) => updTarget(i, "target", v)} rows={2} />
            <MiniArea label="Strategies" value={t.strategies} onChange={(v) => updTarget(i, "strategies", v)} rows={2} />
            <MiniField label="Resources required" value={t.resources} onChange={(v) => updTarget(i, "resources", v)} />
          </div>
        ))}
        <button data-testid="plan-add-target" onClick={addTarget}
          className="no-print text-sm font-semibold rounded-lg border border-dashed px-4 py-2 transition hover:bg-slate-50"
          style={{ borderColor: T.greenDeep, color: T.greenDeep }}>
          + Add blank target
        </button>
      </div>

      <Band>Personnel Involved</Band>
      <Area testId="plan-personnel" value={content.personnel} onChange={(v) => set("personnel", v)} rows={2} />

      <Band>Additional Notes / Home-School Links</Band>
      <Area testId="plan-notes" value={content.notes} onChange={(v) => set("notes", v)} rows={2} />
    </>
  );
}

/* ---------------- Review body ---------------- */
function ReviewBody({ content, set, studentTargets, addReviewTarget }) {
  const updRev = (i, f, v) => set("target_reviews", content.target_reviews.map((t, idx) => (idx === i ? { ...t, [f]: v } : t)));
  const delRev = (i) => set("target_reviews", content.target_reviews.filter((_, idx) => idx !== i));
  const chosenIds = new Set((content.target_reviews || []).map((t) => t.source_target_id).filter(Boolean));
  return (
    <>
      <Band>Review Summary</Band>
      <Area testId="review-summary" value={content.review_summary} onChange={(v) => set("review_summary", v)} rows={3} />

      <Band>Target Outcomes</Band>
      <TargetPicker studentTargets={studentTargets} chosenIds={chosenIds} onAdd={addReviewTarget} testPrefix="review" />
      <div className="space-y-3 mt-3">
        {(content.target_reviews || []).length === 0 && (
          <p className="text-sm text-slate-400">Choose targets above, or Generate with AI.</p>
        )}
        {(content.target_reviews || []).map((t, i) => (
          <div key={t._k || i} data-testid={`review-target-${i}`} className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-bold font-['Outfit']" style={{ color: T.slate }}>{targetLabel(t, i)}</span>
              <button onClick={() => delRev(i)} className="text-xs text-red-400 hover:text-red-600 no-print">Remove</button>
            </div>
            <MiniArea label="Target" value={t.target} onChange={(v) => updRev(i, "target", v)} rows={2} />
            <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mt-2 mb-1">Outcome</p>
            <div className="flex flex-wrap gap-2">
              {OUTCOMES.map((o) => {
                const active = t.outcome === o;
                return (
                  <button key={o} data-testid={`review-outcome-${i}-${o.replace(/\s+/g, "-")}`} onClick={() => updRev(i, "outcome", o)}
                    className="text-sm rounded-lg border px-3 py-1.5 font-medium transition"
                    style={active ? { backgroundColor: OUTCOME_COLOR[o], borderColor: OUTCOME_COLOR[o], color: "#fff" }
                                  : { borderColor: "#cbd5e1", color: "#475569" }}>
                    {o}
                  </button>
                );
              })}
            </div>
            <MiniArea label="Evidence / observations" value={t.evidence} onChange={(v) => updRev(i, "evidence", v)} rows={2} />
          </div>
        ))}
      </div>

      <Band>What Worked Well</Band>
      <Area testId="review-what-worked" value={content.what_worked} onChange={(v) => set("what_worked", v)} rows={3} />

      <Band>What Needs to Change</Band>
      <Area testId="review-what-to-change" value={content.what_to_change} onChange={(v) => set("what_to_change", v)} rows={3} />

      <Band>Next Steps</Band>
      <Area testId="review-next-steps" value={content.next_steps} onChange={(v) => set("next_steps", v)} rows={3} />
    </>
  );
}

/* ---------------- Shared bits ---------------- */
function targetLabel(t, i) {
  if (t.subject && t.area) return `${t.subject} — ${t.area}`;
  if (t.subject) return t.subject;
  if (t.area) return t.area;
  return `Target ${i + 1}`;
}

function TargetPicker({ studentTargets, chosenIds, onAdd, testPrefix }) {
  const available = (studentTargets || []).filter((t) => !chosenIds.has(t.id));
  if (!studentTargets || studentTargets.length === 0) return null;
  return (
    <div className="no-print mt-3 rounded-xl border border-dashed border-slate-200 bg-[#F9F9F7] p-3.5">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
        <Plus size={13} /> Add from this student's priority targets
      </p>
      {available.length === 0 ? (
        <p className="text-xs text-slate-400">All priority targets added.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {available.map((t) => {
            const meta = SUBJECT_META[t.subject];
            return (
              <button key={t.id} data-testid={`${testPrefix}-pick-target-${t.id}`} onClick={() => onAdd(t)}
                className="inline-flex items-center gap-1.5 text-xs rounded-full border bg-white px-3 py-1.5 transition hover:shadow-sm"
                style={{ borderColor: (meta?.tint || "#cbd5e1") + "66", color: "#475569" }}>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta?.tint || "#94a3b8" }} />
                <span className="font-semibold" style={{ color: meta?.tint }}>{meta?.name || t.subject}</span>
                <span className="text-slate-500 max-w-[180px] truncate">{t.text}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Band({ children }) {
  return (
    <h2 className="font-['Outfit'] text-base font-bold mt-7 px-4 py-2 rounded-lg"
      style={{ backgroundColor: BAND_ACCENT.band, color: BAND_ACCENT.bandText }}>
      {children}
    </h2>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
      <p className="font-semibold font-['Outfit']" style={{ color: T.slate }}>{value || "—"}</p>
    </div>
  );
}
function DateField({ label, value, onChange, testId }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</p>
      <input data-testid={testId} type="date" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-slate-300 pb-1 outline-none font-semibold" style={{ color: T.slate }} />
    </div>
  );
}
function Area({ value, onChange, rows, testId }) {
  return (
    <textarea data-testid={testId} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full bg-[#F9F9F7] border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white resize-none transition" />
  );
}
function MiniField({ label, value, onChange }) {
  return (
    <div className="mt-2">
      <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1">{label}</p>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#F9F9F7] border border-slate-100 rounded-lg px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white transition" />
    </div>
  );
}
function MiniArea({ label, value, onChange, rows }) {
  return (
    <div className="mt-2">
      <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1">{label}</p>
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#F9F9F7] border border-slate-100 rounded-lg px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white resize-none transition" />
    </div>
  );
}
function SignLine({ label }) {
  return (
    <div>
      <div className="h-8 border-b border-slate-400" />
      <p className="text-xs text-slate-500 mt-1.5">{label}</p>
    </div>
  );
}
